/**
 * Modal for previewing unpublished changes since last version.
 * This is a read-only view that shows what would be included in the next version release.
 */

import { useState, useEffect } from 'react';
import { Modal, Spin, Alert, Empty, Space, Typography, Descriptions, Pagination } from 'antd';
import { ClockCircleOutlined, FileSearchOutlined } from '@ant-design/icons';
import { previewChanges } from '@/api/versions';
import ChangeRecordItem from './ChangeRecordItem';
import type { PreviewChangesResponse } from '@/api/versions';
import type { RequirementChangeRecord } from '@/types';
import dayjs from 'dayjs';

const { Text } = Typography;

interface PreviewChangesModalProps {
  open: boolean;
  projectId: number;
  onClose: () => void;
}

const PreviewChangesModal: React.FC<PreviewChangesModalProps> = ({
  open,
  projectId,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PreviewChangesResponse | null>(null);
  const [records, setRecords] = useState<RequirementChangeRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    if (open) {
      fetchPreviewData();
    }
  }, [open, projectId]);

  const fetchPreviewData = async (page: number = 1, pageSize: number = 20) => {
    setLoading(true);
    setError(null);

    try {
      const response = await previewChanges(projectId, {
        page,
        page_size: pageSize,
      });

      setData(response);
      setRecords(response.change_records.results);
      setPagination({
        current: response.change_records.page,
        pageSize: response.change_records.page_size,
        total: response.change_records.count,
      });
    } catch (err: unknown) {
      console.error('Failed to preview changes:', err);
      setError(err instanceof Error ? err.message : '加载变更预览失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    fetchPreviewData(page, pageSize || pagination.pageSize);
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('YYYY-MM-DD HH:mm:ss');
  };

  return (
    <Modal
      title="版本变更查看"
      open={open}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnHidden
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          title="加载失败"
          description={error}
          type="error"
          showIcon
        />
      ) : !data?.has_previous_version ? (
        <Empty
          description="暂无版本标签，无法进行变更对比"
          style={{ padding: '50px 0' }}
        />
      ) : data.statistics.total_changes === 0 ? (
        <Empty
          description="当前无未发布的变更"
          style={{ padding: '50px 0' }}
        />
      ) : (
        <div>
          {/* Compare baseline version info */}
          <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item
              label={
                <Space>
                  <ClockCircleOutlined />
                  <span>对比基准版本</span>
                </Space>
              }
            >
              <Space>
                <Text strong>{data.previous_version?.version_number}</Text>
                <Text type="secondary">
                  发布于 {data.previous_version?.published_at && formatDate(data.previous_version.published_at)}
                </Text>
              </Space>
            </Descriptions.Item>

            {/* Change statistics */}
            <Descriptions.Item label="变更统计">
              <Space size="large">
                <Text>
                  总变更数: <Text strong>{data.statistics.total_changes}</Text>
                </Text>
                {data.statistics.created > 0 && (
                  <Text>
                    新增: <Text type="success" strong>{data.statistics.created}</Text>
                  </Text>
                )}
                {data.statistics.updated > 0 && (
                  <Text>
                    修改: <Text type="warning" strong>{data.statistics.updated}</Text>
                  </Text>
                )}
                {data.statistics.deleted > 0 && (
                  <Text>
                    删除: <Text type="danger" strong>{data.statistics.deleted}</Text>
                  </Text>
                )}
              </Space>
            </Descriptions.Item>
          </Descriptions>

          {/* Change records list */}
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            {records.length === 0 ? (
              <Empty
                image={<FileSearchOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
                description="暂无变更记录"
                style={{ padding: '30px 0' }}
              />
            ) : (
              <>
                {records.map((record) => (
                  <ChangeRecordItem key={record.id} record={record} />
                ))}

                {pagination.total > pagination.pageSize && (
                  <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <Pagination
                      current={pagination.current}
                      pageSize={pagination.pageSize}
                      total={pagination.total}
                      onChange={handlePageChange}
                      showSizeChanger
                      showTotal={(total) => `共 ${total} 条变更记录`}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PreviewChangesModal;
